import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import Select from 'react-select';

export function getSelectMenuLayout(rect, viewport, maxHeight = 300, preferredPlacement = 'auto') {
  const gap = 16;
  const below = Math.max(0, viewport.top + viewport.height - rect.bottom - gap);
  const above = Math.max(0, rect.top - viewport.top - gap);
  const preferred = preferredPlacement === 'top' ? 'top' : 'bottom';
  const available = preferred === 'top' ? above : below;
  const placement = available >= maxHeight ? preferred : above > below ? 'top' : 'bottom';
  const width = Math.min(rect.width, Math.max(0, viewport.width - gap));
  return {
    placement,
    maxHeight: Math.min(maxHeight, placement === 'top' ? above : below),
    width,
    left: Math.max(viewport.left + gap / 2, Math.min(rect.left, viewport.left + viewport.width - width - gap / 2)),
  };
}

const ViewportSelect = forwardRef(function ViewportSelect({
  styles = {},
  maxMenuHeight = 300,
  menuPlacement = 'auto',
  onMenuOpen,
  onMenuClose,
  ...props
}, ref) {
  const selectRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [portalTarget, setPortalTarget] = useState(null);
  const [layout, setLayout] = useState(null);
  useImperativeHandle(ref, () => selectRef.current);

  const updateLayout = useCallback(() => {
    const control = selectRef.current?.controlRef;
    if (!control) return;
    const viewport = window.visualViewport;
    const next = getSelectMenuLayout(control.getBoundingClientRect(), {
      top: viewport?.offsetTop || 0,
      left: viewport?.offsetLeft || 0,
      width: viewport?.width || document.documentElement.clientWidth || window.innerWidth,
      height: viewport?.height || window.innerHeight,
    }, maxMenuHeight, menuPlacement);
    setPortalTarget(control.closest('.modal') || document.body);
    setLayout(current => current && Object.keys(next).every(key => current[key] === next[key]) ? current : next);
  }, [maxMenuHeight, menuPlacement]);

  useEffect(() => {
    if (!open) return undefined;
    updateLayout();
    const viewport = window.visualViewport;
    window.addEventListener('resize', updateLayout);
    document.addEventListener('scroll', updateLayout, true);
    viewport?.addEventListener('resize', updateLayout);
    viewport?.addEventListener('scroll', updateLayout);
    return () => {
      window.removeEventListener('resize', updateLayout);
      document.removeEventListener('scroll', updateLayout, true);
      viewport?.removeEventListener('resize', updateLayout);
      viewport?.removeEventListener('scroll', updateLayout);
    };
  }, [open, updateLayout]);

  return (
    <Select
      {...props}
      ref={selectRef}
      menuPortalTarget={portalTarget}
      menuPosition="fixed"
      menuPlacement={layout?.placement || 'auto'}
      maxMenuHeight={layout?.maxHeight ?? maxMenuHeight}
      minMenuHeight={0}
      menuShouldScrollIntoView={false}
      onMenuOpen={() => {
        updateLayout();
        setOpen(true);
        onMenuOpen?.();
      }}
      onMenuClose={() => {
        setOpen(false);
        onMenuClose?.();
      }}
      styles={{
        ...styles,
        menuPortal: (base, state) => ({
          ...(styles.menuPortal ? styles.menuPortal(base, state) : base),
          zIndex: 2000,
          ...(layout ? { left: layout.left, width: layout.width } : {}),
        }),
        menuList: (base, state) => ({
          ...(styles.menuList ? styles.menuList(base, state) : base),
          maxHeight: layout?.maxHeight ?? maxMenuHeight,
          overflowY: 'auto',
          overflowX: 'hidden',
          overscrollBehavior: 'contain',
        }),
        option: (base, state) => ({
          ...(styles.option ? styles.option(base, state) : base),
          whiteSpace: 'normal',
          overflowWrap: 'anywhere',
        }),
      }}
    />
  );
});

export default ViewportSelect;
